import { EventEmitter } from '../utils/event-emitter.js';
import { querySelector, debounce } from '../utils/helpers.js';
import {
  SELECTORS,
  EVENTS,
  SEARCH_CONFIG,
} from '../utils/constants.js';

/**
 * Менеджер поиска
 * Отвечает за обработку поисковых запросов с debounce и улучшенным UX
 */
export class SearchManager extends EventEmitter {
  constructor() {
    super();
    this.searchInput = null;
    this.currentQuery = '';
    this.debouncedSearch = null;
    this.isSearching = false;
    this.eventsInitialized = false;
  }

  /**
   * Инициализация менеджера
   */
  init() {
    this.searchInput = querySelector(SELECTORS.SEARCH_INPUT);

    if (!this.searchInput) {
      throw new Error('Поле поиска не найдено');
    }

    // Создаем debounced функцию поиска
    this.debouncedSearch = debounce(
      this.performSearch.bind(this),
      SEARCH_CONFIG.DEBOUNCE_DELAY,
    );

    this.setupEventListeners();

    console.log('🔍 SearchManager инициализирован');
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    if (!this.searchInput || this.eventsInitialized) return;

    // Основной обработчик ввода
    this.searchInput.addEventListener('input', (event) => {
      const query = event.target.value;
      this.handleSearchInput(query);
    });

    // Обработчик нажатия клавиш
    this.searchInput.addEventListener('keydown', (event) => {
      this.handleKeyDown(event);
    });

    // Обработчик фокуса
    this.searchInput.addEventListener('focus', () => {
      this.handleFocus();
    });

    // Обработчик потери фокуса
    this.searchInput.addEventListener('blur', () => {
      this.handleBlur();
    });

    // Обработчик paste события
    this.searchInput.addEventListener('paste', (event) => {
      // Небольшая задержка для получения вставленного текста
      setTimeout(() => {
        const query = event.target.value;
        this.handleSearchInput(query);
      }, 10);
    });

    this.eventsInitialized = true;
  }

  /**
   * Обработка ввода в поле поиска
   * @param {string} query - Поисковый запрос
   */
  handleSearchInput(query) {
    const trimmedQuery = query.trim();

    // Обновляем текущий запрос
    this.currentQuery = trimmedQuery;

    // Показываем индикатор загрузки для длинных запросов
    if (trimmedQuery.length > 3) {
      this.setSearchingState(true);
    }

    // Выполняем поиск с debounce
    this.debouncedSearch(trimmedQuery);

    // Обновляем UI поля поиска
    this.updateSearchInputState(trimmedQuery);
  }

  /**
   * Выполнение поиска
   * @param {string} query - Поисковый запрос
   */
  performSearch(query) {
    // Убираем индикатор загрузки
    this.setSearchingState(false);

    // Проверяем минимальную длину запроса
    if (query.length > 0 && query.length < SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      return;
    }

    // Уведомляем о выполнении поиска
    this.emit(EVENTS.SEARCH, query);

    // Сохраняем запрос в истории (для будущего использования)
    this.addToSearchHistory(query);
  }

  /**
   * Обработка нажатия клавиш
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  handleKeyDown(event) {
    switch (event.key) {
    case 'Enter':
      // Принудительный поиск при нажатии Enter
      event.preventDefault();
      this.performSearch(this.currentQuery);
      break;

    case 'Escape':
      // Очистка поиска при нажатии Escape
      this.clearSearch();
      break;

    case 'ArrowDown':
    case 'ArrowUp':
      // Для будущей реализации автокомплита
      event.preventDefault();
      this.handleArrowNavigation(event.key);
      break;
    }
  }

  /**
   * Обработка получения фокуса
   */
  handleFocus() {
    this.searchInput.parentElement?.classList.add('search-focused');

    // Показываем подсказки или историю поиска (для будущего)
    this.showSearchSuggestions();
  }

  /**
   * Обработка потери фокуса
   */
  handleBlur() {
    this.searchInput.parentElement?.classList.remove('search-focused');

    // Скрываем подсказки с небольшой задержкой
    setTimeout(() => {
      this.hideSearchSuggestions();
    }, 150);
  }

  /**
   * Обработка навигации стрелками (для автокомплита)
   * @param {string} direction - Направление (ArrowUp/ArrowDown)
   */
  handleArrowNavigation(direction) {
    // Заготовка для будущей реализации автокомплита
    console.log(`Arrow navigation: ${direction}`);
  }

  /**
   * Обновление состояния поля поиска
   * @param {string} query - Текущий запрос
   */
  updateSearchInputState(query) {
    if (!this.searchInput) return;

    const container = this.searchInput.parentElement;
    if (!container) return;

    // Добавляем/убираем класс для пустого состояния
    if (query.length === 0) {
      container.classList.remove('search-has-value');
      container.classList.add('search-empty');
    } else {
      container.classList.add('search-has-value');
      container.classList.remove('search-empty');
    }

    // Обновляем placeholder в зависимости от состояния
    this.updatePlaceholder(query);
  }

  /**
   * Обновление placeholder текста
   * @param {string} query - Текущий запрос
   */
  updatePlaceholder(query) {
    if (!this.searchInput) return;

    if (query.length === 0) {
      this.searchInput.placeholder = 'Поиск фильмов...';
    } else if (query.length < SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      this.searchInput.placeholder = `Введите минимум ${SEARCH_CONFIG.MIN_QUERY_LENGTH} символа`;
    }
  }

  /**
   * Установка состояния загрузки
   * @param {boolean} isSearching - Состояние поиска
   */
  setSearchingState(isSearching) {
    this.isSearching = isSearching;

    const container = this.searchInput?.parentElement;
    if (!container) return;

    if (isSearching) {
      container.classList.add('search-loading');
    } else {
      container.classList.remove('search-loading');
    }
  }

  /**
   * Очистка поиска
   */
  clearSearch() {
    if (!this.searchInput) return;

    this.searchInput.value = '';
    this.currentQuery = '';
    this.setSearchingState(false);
    this.updateSearchInputState('');

    // Уведомляем об очистке
    this.emit(EVENTS.SEARCH_CLEAR);
    this.emit(EVENTS.SEARCH, '');
  }

  /**
   * Программная установка поискового запроса
   * @param {string} query - Поисковый запрос
   * @param {boolean} triggerSearch - Выполнить поиск немедленно
   */
  setQuery(query, triggerSearch = true) {
    if (!this.searchInput) return;

    this.searchInput.value = query;
    this.currentQuery = query.trim();
    this.updateSearchInputState(this.currentQuery);

    if (triggerSearch) {
      this.performSearch(this.currentQuery);
    }
  }

  /**
   * Получение текущего поискового запроса
   * @returns {string} - Текущий запрос
   */
  getQuery() {
    return this.currentQuery;
  }

  /**
   * Проверка, активен ли поиск
   * @returns {boolean} - Состояние поиска
   */
  isActive() {
    return this.currentQuery.length > 0;
  }

  /**
   * Фокус на поле поиска
   */
  focus() {
    if (this.searchInput) {
      this.searchInput.focus();
    }
  }

  /**
   * Размытие поля поиска
   */
  blur() {
    if (this.searchInput) {
      this.searchInput.blur();
    }
  }

  /**
   * Добавление в историю поиска (заготовка)
   * @param {string} query - Поисковый запрос
   */
  addToSearchHistory(query) {
    if (!query || query.length < SEARCH_CONFIG.MIN_QUERY_LENGTH) return;

    // Получаем историю из localStorage
    const history = this.getSearchHistory();

    // Добавляем новый запрос (избегаем дубликатов)
    const updatedHistory = [query, ...history.filter(item => item !== query)]
      .slice(0, 10); // Храним максимум 10 запросов

    // Сохраняем в localStorage
    try {
      localStorage.setItem('searchHistory', JSON.stringify(updatedHistory));
    } catch (error) {
      console.warn('Не удалось сохранить историю поиска:', error);
    }
  }

  /**
   * Получение истории поиска
   * @returns {Array} - Массив предыдущих запросов
   */
  getSearchHistory() {
    try {
      const history = localStorage.getItem('searchHistory');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.warn('Не удалось загрузить историю поиска:', error);
      return [];
    }
  }

  /**
   * Очистка истории поиска
   */
  clearSearchHistory() {
    try {
      localStorage.removeItem('searchHistory');
    } catch (error) {
      console.warn('Не удалось очистить историю поиска:', error);
    }
  }

  /**
   * Показ подсказок поиска (заготовка для автокомплита)
   */
  showSearchSuggestions() {
    // Заготовка для будущей реализации
    const history = this.getSearchHistory();
    if (history.length > 0 && this.currentQuery.length === 0) {
      console.log('Показать историю поиска:', history);
    }
  }

  /**
   * Скрытие подсказок поиска
   */
  hideSearchSuggestions() {
    // Заготовка для будущей реализации
    console.log('Скрыть подсказки поиска');
  }

  /**
   * Получение статистики поиска
   * @returns {Object} - Статистика
   */
  getStatistics() {
    return {
      currentQuery: this.currentQuery,
      queryLength: this.currentQuery.length,
      isActive: this.isActive(),
      isSearching: this.isSearching,
      historyCount: this.getSearchHistory().length,
    };
  }

  /**
   * Деструктор
   */
  destroy() {
    // Отменяем pending debounced вызовы
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel?.();
    }

    this.removeAllListeners();
    this.searchInput = null;
    this.currentQuery = '';
    this.debouncedSearch = null;
    this.isSearching = false;
  }
}
